import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { BidDetailsSidebarView, WhatsappMessageParams } from '../../models/bid-details-sidebar.type';
import { AppSnackbarService } from '../../../../core/notifications/snackbar.service';
import { MESSAGE_KEYS } from '../../../../shared/constants/messages';
import { isBidApprovalBlocked } from '../../../../shared/constants/offer-status.constants';
import { OfferChatComponent } from '../offer-chat/offer-chat.component';
import { BidDetailsSidebarService } from '../../services/bid-details-sidebar.service';
import { GenericDialogService } from '../../../../core/services/generic-dialog.service';
import { AuthStore } from '../../../../store/auth.store';
import { WhatsappMessageDialogComponent } from '../../../../shared/ui/dialogs/whatsapp-message-dialog/whatsapp-message-dialog.component';
import { TippyDirective } from '../../../../shared/directives/tippy.directive';

@Component({
  selector: 'app-bid-details-sidebar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, TranslateModule, OfferChatComponent, TippyDirective],
  templateUrl: './bid-details-sidebar.component.html',
  styleUrl: './bid-details-sidebar.component.scss',
})
export class BidDetailsSidebarComponent {
  private readonly bidSidebarService = inject(BidDetailsSidebarService);
  private readonly snackbarService = inject(AppSnackbarService);
  private readonly translateService = inject(TranslateService);
  private readonly genericDialog = inject(GenericDialogService);
  private readonly authStore = inject(AuthStore);

  readonly data = input.required<BidDetailsSidebarView>();

  readonly closeSidebar = output<void>();
  readonly approveBid = output<string>();

  readonly isDatesSectionOpen = signal(true);
  readonly isObservationsSectionOpen = signal(true);
  readonly isCarrierSectionOpen = signal(true);
  readonly isChatSectionOpen = signal(false);
  private lastHandledAutoOpenBidId: string | null = null;

  readonly chatBadgeValue = computed(() => {
    const count = this.data().unreadMessages;
    return count > 99 ? '99+' : String(count);
  });

  readonly canApproveBid = computed(() => !isBidApprovalBlocked(this.data().offerStatusCode));

  constructor() {
    effect(() => {
      const sidebarData = this.data();
      if (!sidebarData.openChatOnOpen) {
        return;
      }

      if (this.lastHandledAutoOpenBidId === sidebarData.bidId) {
        return;
      }

      this.lastHandledAutoOpenBidId = sidebarData.bidId;
      this.isChatSectionOpen.set(true);
      this.focusChatInputWithRetry();
    });

    effect(() => {
      const request = this.bidSidebarService.chatOpenRequest();
      if (!request) {
        return;
      }

      if (request.bidId !== this.data().bidId) {
        return;
      }

      this.isChatSectionOpen.set(true);
      this.focusChatInputWithRetry();
      this.bidSidebarService.clearChatOpenRequest();
    });
  }

  onClose(): void {
    this.lastHandledAutoOpenBidId = null;
    this.bidSidebarService.clearChatOpenRequest();
    this.closeSidebar.emit();
  }

  toggleDatesSection(): void {
    this.isDatesSectionOpen.update((value) => !value);
  }

  toggleObservationsSection(): void {
    this.isObservationsSectionOpen.update((value) => !value);
  }

  toggleCarrierSection(): void {
    this.isCarrierSectionOpen.update((value) => !value);
  }

  toggleChatSection(): void {
    const willOpen = !this.isChatSectionOpen();
    this.isChatSectionOpen.set(willOpen);
    if (willOpen) {
      this.focusChatInputWithRetry();
    }
  }

  onApproveBid(): void {
    // Validate bid value like the table approval flow
    if (!this.data()?.hasBidValue) {
      this.snackbarService.warning(
        this.translateService.instant(MESSAGE_KEYS.BIDS.APPROVE_NO_VALUE)
      );
      return;
    }

    this.approveBid.emit(this.data().bidId);
  }

  onNegotiate(): void {
    // Open local chat accordion
    this.isChatSectionOpen.set(true);
    this.focusChatInputWithRetry();

    // Also request global chat open so other parts of the UI
    // (or the same sidebar when opened from elsewhere) can react.
    try {
      const bidId = this.data()?.bidId;
      if (bidId) {
        this.bidSidebarService.requestChatOpen(bidId);
      }
    } catch {
      // noop
    }
  }

  onWhatsapp(): void {
    const phoneItem = this.data().carrierInfo.find((item) => item.id === 'phone');
    const messageParams: WhatsappMessageParams = {
      carrierName: this.data().carrierName,
      userName: this.authStore.currentUser()?.name ?? '',
      companyName: this.data().shipperName ?? '',
      offerNumber: this.data().offerNumber,
    };
    const message = this.translateService.instant(
      'bids.sidebar.carrier.whatsapp_message_template',
      messageParams,
    );

    this.genericDialog.open(
      {
        title: 'whatsapp_dialog.title',
        svgIcon: 'whatsapp',
        iconTheme: 'success',
        showCloseButton: true,
        disableClose: false,
        maxWidth: '480px',
        contentData: { phone: phoneItem?.value ?? null, message, contactName: this.data().carrierName },
        buttons: [],
      },
      WhatsappMessageDialogComponent,
    );
  }

  trackById(index: number, item: { id: string }): string {
    return `${item.id}-${index}`;
  }

  getTimelineIconClass(variant: string): string {
    return `timeline-icon timeline-icon--${variant}`;
  }

  getCarrierInfoIconClass(variant: string): string {
    return `carrier-info-icon carrier-info-icon--${variant}`;
  }

  private focusChatInputWithRetry(): void {
    const maxAttempts = 15;
    const intervalMs = 80;
    let attempts = 0;

    const tryFocus = () => {
      attempts += 1;
      const input = document.getElementById('offer-chat-input') as HTMLInputElement | null;
      if (input) {
        input.focus();
        return;
      }

      if (attempts < maxAttempts) {
        setTimeout(tryFocus, intervalMs);
      }
    };

    setTimeout(tryFocus, 0);
  }
}
